/**
 * Viewer-link API (F2). Mirrors PRD §7:
 *   POST   /sessions/{id}/viewer-link   (owner, JWT) → create a share link
 *   DELETE /viewer-link/{token}         (owner, JWT) → revoke a link
 *
 * The returned token is what the public viewer page consumes (route
 * `/viewer/:token`, WS `/ws/viewer/{token}`). No API key is ever exposed here —
 * only an opaque link token.
 */

import { http } from '@/shared/api/http';

/** A viewer link row (PRD §6 `viewer_links`). */
export interface ViewerLink {
  token: string;
  session_id: string;
  /** ISO timestamp, or null for a non-expiring link. */
  expires_at: string | null;
  revoked: boolean;
}

export const viewerLinkApi = {
  /** Create a share link for a session (owner only). */
  create(sessionId: string): Promise<ViewerLink> {
    return http.post<ViewerLink>(
      `/sessions/${encodeURIComponent(sessionId)}/viewer-link`,
    );
  },

  /** Revoke a previously issued link. */
  revoke(token: string): Promise<void> {
    return http.delete<void>(`/viewer-link/${encodeURIComponent(token)}`);
  },
};

/**
 * Build the absolute public viewer URL for a token, using the current origin.
 * This is what gets encoded into the QR code and copied to the clipboard.
 */
export function buildViewerUrl(token: string): string {
  const origin =
    typeof window !== 'undefined' && window.location
      ? window.location.origin
      : '';
  return `${origin}/viewer/${encodeURIComponent(token)}`;
}
